import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getBottles, addBottle, addRandomBottle, releaseBottle, createCheckout, refreshHold, sendReferral } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ShoppingCart, X, Settings, Send, Shuffle, Wine } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_solaro-founding/artifacts/6pe0ovti_Solaro%20Capri%20Gin%20-%20Horizontal%20Vectorial%20Handpainted%20PNG%20TRANSPARENT.png";

const PRICE_RANDOM = 30;
const PRICE_SPECIFIC = 50;
const HOLD_MINUTES = 15;

function BottleItem({ bottle, isOwn, onClick, onHover, onLeave }) {
  const isTaken = bottle.status !== "available" && !isOwn;

  const statusClasses = {
    available: "border-2 border-[#002366]/15 bg-white hover:border-[#002366] hover:shadow-md cursor-pointer text-[#002366]",
    held: bottle.holder_id && isOwn
      ? "bg-[#D4AF37] text-white border-none shadow-lg shadow-[#D4AF37]/30 cursor-pointer z-10"
      : "bg-[#C4825A]/20 border-2 border-[#C4825A]/50 bottle-held cursor-default text-[#C4825A]/70",
    confirmed: isOwn
      ? "bg-[#16663A] text-white border-none shadow-lg cursor-pointer"
      : "bg-[#4A4A4A] text-white/40 border-none cursor-default",
  };

  return (
    <button
      data-testid={`bottle-${bottle.number}`}
      onClick={onClick}
      onMouseEnter={(e) => isTaken && onHover(bottle, e)}
      onMouseLeave={onLeave}
      onTouchStart={(e) => isTaken && onHover(bottle, e)}
      onTouchEnd={() => setTimeout(onLeave, 1500)}
      disabled={false}
      className={`bottle-shape flex flex-col items-center justify-center relative ${statusClasses[bottle.status] || statusClasses.available} transition-all duration-300`}
    >
      <span className="font-display text-[10px] sm:text-xs md:text-sm font-bold leading-none">
        {bottle.number}
      </span>
      {isOwn && bottle.status === "held" && (
        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#D4AF37] rounded-full border-2 border-white" />
      )}
      {isOwn && bottle.status === "confirmed" && (
        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#16663A] rounded-full border-2 border-white" />
      )}
    </button>
  );
}

function CountdownTimer({ heldAt, onExpired }) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!heldAt) return;
    const heldTime = new Date(heldAt).getTime();
    const expiresAt = heldTime + HOLD_MINUTES * 60 * 1000;

    const tick = () => {
      const now = Date.now();
      const diff = Math.max(0, expiresAt - now);
      setTimeLeft(diff);
      if (diff <= 0) {
        onExpired();
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [heldAt, onExpired]);

  if (timeLeft === null) return null;

  const mins = Math.floor(timeLeft / 60000);
  const secs = Math.floor((timeLeft % 60000) / 1000);

  return (
    <span className={`font-mono font-bold ${mins < 2 ? 'text-red-500' : 'text-[#002366]'}`}>
      {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
    </span>
  );
}

export default function GridPage() {
  const { user, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [bottles, setBottles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [addingRandom, setAddingRandom] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [referralEmail, setReferralEmail] = useState("");
  const [referralWhatsapp, setReferralWhatsapp] = useState("");
  const [referralSending, setReferralSending] = useState(false);
  const [referralTargetBottle, setReferralTargetBottle] = useState(null); // bottle number for specific unlock
  const expiredHandled = useRef(false);

  const handleTooltipShow = (bottle, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ bottle, x: rect.left + rect.width / 2, y: rect.top });
  };
  const handleTooltipHide = () => setTooltip(null);

  const fetchBottles = useCallback(async () => {
    try {
      const res = await getBottles();
      setBottles(res.data.bottles || []);
    } catch {
      toast.error("Failed to load bottles");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBottles();
    const interval = setInterval(fetchBottles, 15000);
    return () => clearInterval(interval);
  }, [fetchBottles]);

  // Show welcome popup on first visit
  useEffect(() => {
    if (user?.assigned_bottle && !loading && bottles.length > 0) {
      const key = `solaro_welcome_${user.id}`;
      if (!sessionStorage.getItem(key)) {
        setShowWelcome(true);
        sessionStorage.setItem(key, "true");
      }
    }
  }, [user, loading, bottles]);

  const myBottles = bottles.filter((b) => b.holder_id === user?.id);
  const myHeld = myBottles.filter((b) => b.status === "held");
  const myConfirmed = myBottles.filter((b) => b.status === "confirmed");
  const totalTaken = bottles.filter((b) => b.status !== "available").length;
  const totalBottles = bottles.length;
  const maxBottles = user?.max_bottles || 6;

  // Find the first random held bottle for countdown
  const primaryHeld = myHeld.find(b => b.selection_type !== "specific") || myHeld[0];

  const handleTimerExpired = useCallback(async () => {
    if (expiredHandled.current) return;
    expiredHandled.current = true;
    try {
      const res = await refreshHold();
      toast.info(`Time's up! New number assigned: #${res.data.bottle_number}`);
      fetchBottles();
      fetchUser();
    } catch {
      toast.error("Could not refresh your hold");
    }
    setTimeout(() => { expiredHandled.current = false; }, 5000);
  }, [fetchBottles, fetchUser]);

  const handleBottleClick = async (bottle) => {
    if (bottle.holder_id === user?.id) {
      setShowCart(true);
      return;
    }
    if (bottle.status !== "available") return;

    if (myBottles.length >= maxBottles) {
      toast.error(`Maximum ${maxBottles} bottles per person`);
      return;
    }
    // Specific selection requires referral
    setReferralTargetBottle(bottle.number);
    setReferralEmail("");
    setReferralWhatsapp("");
    setShowReferral(true);
  };

  const handleAddRandom = async () => {
    if (myBottles.length >= maxBottles) {
      toast.error(`Maximum ${maxBottles} bottles per person`);
      return;
    }
    setAddingRandom(true);
    try {
      const res = await addRandomBottle();
      toast.success(`Bottle #${res.data.bottle_number} added randomly`);
      fetchBottles();
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add bottle");
    }
    setAddingRandom(false);
  };

  const handleRelease = async (number) => {
    try {
      await releaseBottle(number);
      toast.success(`Bottle #${number} released`);
      fetchBottles();
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not release bottle");
    }
  };

  const calculateTotal = () => {
    let total = 0;
    myHeld.forEach((b) => {
      const selType = b.selection_type || "random";
      total += selType === "specific" ? PRICE_SPECIFIC : PRICE_RANDOM;
    });
    return total;
  };

  const handleCheckout = async () => {
    if (myHeld.length === 0) {
      toast.error("No bottles to confirm");
      return;
    }
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const res = await createCheckout({
        origin_url: origin,
        bottle_numbers: myHeld.map((b) => b.number),
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    }
    setCheckoutLoading(false);
  };

  const handleSendReferral = async () => {
    if (!referralEmail && !referralWhatsapp) {
      toast.error("Enter an email or WhatsApp number");
      return;
    }
    setReferralSending(true);
    try {
      const res = await sendReferral({
        email: referralEmail || null,
        whatsapp: referralWhatsapp || null,
        bottle_number: referralTargetBottle || null,
      });
      if (referralTargetBottle && res.data.bottle_assigned) {
        toast.success(`Invite sent! Bottle #${referralTargetBottle} is now yours (€50 specific).`);
      } else if (referralTargetBottle && !res.data.bottle_assigned) {
        toast.info(`Invite sent! However, bottle #${referralTargetBottle} is no longer available.`);
      } else {
        toast.success("Invite sent!");
      }
      setReferralEmail("");
      setReferralWhatsapp("");
      setShowReferral(false);
      setReferralTargetBottle(null);
      fetchBottles();
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send referral");
    }
    setReferralSending(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="grid-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-[#1A2B4C]/50 tracking-wide text-sm uppercase">Loading bottles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="grid-page">
      {/* Welcome Popup */}
      {showWelcome && user?.assigned_bottle && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" data-testid="welcome-popup">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-sm bg-white shadow-2xl p-8 text-center animate-fade-in-up">
            <img src={LOGO_URL} alt="Solaro" className="h-10 mx-auto mb-6 object-contain" />
            <p className="font-body text-xs uppercase tracking-widest text-[#1A2B4C]/40 mb-2">Your number is</p>
            <p className="font-display text-6xl sm:text-7xl text-[#002366] mb-2" data-testid="welcome-bottle-number">
              {user.assigned_bottle}
            </p>
            <div className="w-12 h-0.5 bg-[#D4AF37] mx-auto mb-4" />
            <p className="font-body text-sm text-[#1A2B4C]/60 mb-8 leading-relaxed">
              This number has been reserved just for you. Welcome to the Circolo.
            </p>
            <Button
              data-testid="welcome-proceed-btn"
              onClick={() => setShowWelcome(false)}
              className="w-full bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-sm px-8 py-5"
            >
              Proceed into the Circolo
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#D1CFC7] px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="Solaro" className="h-6 sm:h-10 object-contain" data-testid="grid-logo" />
          <div className="flex items-center gap-2 sm:gap-6">
            {/* Add Random Bottle button */}
            {myBottles.length < maxBottles && (
              <Button
                data-testid="add-random-btn"
                onClick={handleAddRandom}
                disabled={addingRandom}
                variant="outline"
                className="border-[#D4AF37] text-[#002366] hover:bg-[#D4AF37] hover:text-white rounded-none text-[10px] sm:text-xs uppercase tracking-widest px-2 sm:px-5 py-1.5 sm:py-2 transition-all"
              >
                <Shuffle size={14} className="sm:mr-2" />
                <span className="hidden sm:inline">Add Bottle</span>
              </Button>
            )}
            <button
              data-testid="my-reservations-btn"
              onClick={() => navigate("/reservations")}
              className="p-1.5 sm:p-0 text-[#002366]/60 sm:text-[#002366] hover:text-[#D4AF37] transition-colors"
              title="My Bottles"
            >
              <Wine size={18} className="sm:hidden" />
              <span className="hidden sm:inline text-xs uppercase tracking-widest font-body">My Bottles</span>
            </button>
            <button
              data-testid="settings-btn"
              onClick={() => navigate("/settings")}
              className="p-1.5 text-[#002366]/60 hover:text-[#D4AF37] transition-colors"
            >
              <Settings size={18} />
            </button>
            <button
              data-testid="cart-btn"
              onClick={() => setShowCart(!showCart)}
              className="relative p-1.5 text-[#002366] hover:text-[#D4AF37] transition-colors"
            >
              <ShoppingCart size={20} />
              {myHeld.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-[#D4AF37] text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center font-bold">
                  {myHeld.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-10">
        {/* Hero section */}
        <div className="text-center mb-4 sm:mb-10 animate-fade-in-up">
          <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl text-[#002366] mb-2 sm:mb-3 tracking-tight">
            Choose Your Number
          </h1>

          {/* Your number */}
          {user?.assigned_bottle && (
            <p className="font-display text-base sm:text-lg text-[#D4AF37] mb-1" data-testid="your-number-text">
              Your number is {user.assigned_bottle}
            </p>
          )}

          {/* Reserved counter */}
          <p className="font-body text-xs sm:text-sm text-[#1A2B4C]/50 mb-1" data-testid="reserved-counter">
            <span className="font-display text-sm sm:text-base text-[#002366]">{totalTaken}</span> of {totalBottles} Already Reserved
          </p>

          {/* Countdown timer */}
          {primaryHeld && primaryHeld.held_at && (
            <p className="font-body text-xs sm:text-sm text-[#1A2B4C]/50 mb-2" data-testid="hold-timer">
              Your #{primaryHeld.number} number is held for{" "}
              <CountdownTimer heldAt={primaryHeld.held_at} onExpired={handleTimerExpired} />
            </p>
          )}

          {/* Instruction text + refer a friend */}
          <p className="font-body text-xs sm:text-sm text-[#1A2B4C]/60 max-w-lg mx-auto leading-relaxed mt-2">
            If you want a specific number, tap it directly on the grid.
            <br className="hidden sm:block" />{" "}
            For more bottles,{" "}
            <button
              data-testid="refer-friend-link"
              onClick={() => { setReferralTargetBottle(null); setShowReferral(true); }}
              className="text-[#002366] font-semibold underline underline-offset-4 hover:text-[#D4AF37] transition-colors"
            >
              refer a friend
            </button>
          </p>
        </div>

        {/* Referral / Invite a friend Modal */}
        {showReferral && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" data-testid="referral-modal">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setShowReferral(false); setReferralTargetBottle(null); }} />
            <div className="relative w-full max-w-sm bg-white shadow-2xl p-6 sm:p-8 animate-fade-in-up">
              <button onClick={() => { setShowReferral(false); setReferralTargetBottle(null); }} className="absolute top-4 right-4 text-[#1A2B4C]/30 hover:text-[#002366]">
                <X size={18} />
              </button>
              <h3 className="font-display text-xl text-[#002366] mb-1">Invite a Friend</h3>
              {referralTargetBottle ? (
                <p className="font-body text-sm text-[#1A2B4C]/60 mb-5">
                  Send an invite to unlock bottle <strong className="text-[#002366]">#{referralTargetBottle}</strong> (€50 specific selection).
                </p>
              ) : (
                <p className="font-body text-sm text-[#1A2B4C]/60 mb-5">
                  Invite a friend to join the Circolo Solaro.
                </p>
              )}
              <div className="space-y-3">
                <Input
                  data-testid="referral-email-input"
                  type="email"
                  value={referralEmail}
                  onChange={(e) => setReferralEmail(e.target.value)}
                  placeholder="Friend's email"
                  className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white text-sm"
                />
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-[#D1CFC7]" />
                  <span className="text-xs text-[#1A2B4C]/30 font-body">or</span>
                  <div className="flex-1 h-px bg-[#D1CFC7]" />
                </div>
                <Input
                  data-testid="referral-whatsapp-input"
                  type="text"
                  value={referralWhatsapp}
                  onChange={(e) => setReferralWhatsapp(e.target.value)}
                  placeholder="Friend's WhatsApp (+39...)"
                  className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white text-sm"
                />
                <Button
                  data-testid="referral-send-btn"
                  onClick={handleSendReferral}
                  disabled={referralSending || (!referralEmail && !referralWhatsapp)}
                  className="w-full bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-xs px-6 py-4 mt-2"
                >
                  <Send size={14} className="mr-2" />
                  {referralSending ? "Sending..." : referralTargetBottle ? `Send Invite & Unlock #${referralTargetBottle}` : "Send Invite"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Legend — always visible */}
        <div className="mb-4 sm:mb-6" data-testid="legend-section">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-5">
            <div className="flex items-center gap-2">
              <div className="w-5 h-7 sm:w-6 sm:h-8 border-2 border-[#002366]/15 bg-white rounded-t-full rounded-b-sm" />
              <span className="text-[10px] sm:text-xs font-body text-[#1A2B4C]/60">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-7 sm:w-6 sm:h-8 bg-[#D4AF37] rounded-t-full rounded-b-sm" />
              <span className="text-[10px] sm:text-xs font-body text-[#1A2B4C]/60">Your Hold (15 min)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-7 sm:w-6 sm:h-8 bg-[#C4825A]/20 border-2 border-[#C4825A]/50 rounded-t-full rounded-b-sm" />
              <span className="text-[10px] sm:text-xs font-body text-[#1A2B4C]/60">Held</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-7 sm:w-6 sm:h-8 bg-[#16663A] rounded-t-full rounded-b-sm" />
              <span className="text-[10px] sm:text-xs font-body text-[#1A2B4C]/60">Your Confirmed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-7 sm:w-6 sm:h-8 bg-[#4A4A4A] rounded-t-full rounded-b-sm" />
              <span className="text-[10px] sm:text-xs font-body text-[#1A2B4C]/60">Unavailable Confirmed</span>
            </div>
          </div>
        </div>

        {/* The Grid */}
        <div
          data-testid="bottle-grid"
          className="grid gap-1 sm:gap-1.5 md:gap-2"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))" }}
        >
          {bottles.map((bottle) => (
            <BottleItem
              key={bottle.number}
              bottle={bottle}
              isOwn={bottle.holder_id === user?.id}
              onClick={() => handleBottleClick(bottle)}
              onHover={handleTooltipShow}
              onLeave={handleTooltipHide}
            />
          ))}
        </div>

        {/* Floating tooltip */}
        {tooltip && (
          <div
            data-testid="bottle-tooltip"
            className="fixed z-50 pointer-events-none"
            style={{ left: tooltip.x, top: tooltip.y - 10, transform: "translate(-50%, -100%)" }}
          >
            <div className="bg-[#002366] text-white px-3 py-1.5 text-xs font-body whitespace-nowrap shadow-lg rounded-sm">
              {tooltip.bottle.holder_name || "Reserved"}
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#002366]" />
            </div>
          </div>
        )}

        {/* How This Works */}
        <div className="mt-10 mb-24 bg-white p-5 sm:p-8 shadow-sm max-w-2xl mx-auto">
          <h3 className="font-display text-lg sm:text-xl text-[#002366] mb-3">How This Works</h3>
          <div className="space-y-2 font-body text-xs sm:text-sm text-[#1A2B4C]/70 leading-relaxed">
            <p>Your bottle is held for 15 minutes.</p>
            <p>When the deposit is paid, your number is confirmed.</p>
            <p>
              <strong className="text-[#002366]">Random number:</strong> &#8364;30 deposit — use the <strong>"Add Bottle"</strong> button, if available, for additional bottles.
            </p>
            <p>
              <strong className="text-[#002366]">Specific number:</strong> &#8364;50 deposit — tap directly on the grid.
            </p>
            <p>
              For more bottles,{" "}
              <button
                onClick={() => { setReferralTargetBottle(null); setShowReferral(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="text-[#002366] font-semibold underline underline-offset-4 hover:text-[#D4AF37] transition-colors"
              >
                refer a friend
              </button>.
            </p>
            <p>All deposits are fully redeemable against your final order.</p>
          </div>
          <div className="mt-4 p-3 sm:p-4 bg-[#F9F7F2] border border-[#D1CFC7]">
            <h4 className="font-display text-xs sm:text-sm text-[#002366] mb-2 uppercase tracking-wider">Every Bottle Includes</h4>
            <ul className="space-y-1 font-body text-xs sm:text-sm text-[#1A2B4C]/70">
              <li>Il Versatore — the Solaro ritual glass pourer (&#8364;30 value)</li>
              <li>A signed postcard from Lorenzo</li>
              <li>Founding Member of Circolo Solaro</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Sticky Bottom CTA Bar */}
      {myHeld.length > 0 && !showCart && (
        <div
          data-testid="sticky-reserve-bar"
          className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#D1CFC7] shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-4 py-3 sm:py-4 animate-fade-in-up"
        >
          <div className="max-w-xl mx-auto flex items-center justify-between gap-3">
            <div className="flex-shrink-0">
              <p className="font-body text-xs text-[#1A2B4C]/50 uppercase tracking-widest">
                {myHeld.length} {myHeld.length === 1 ? "bottle" : "bottles"} selected
              </p>
              <p className="font-display text-lg sm:text-xl text-[#002366]">
                &#8364;{calculateTotal()} deposit
              </p>
            </div>
            <Button
              data-testid="sticky-reserve-btn"
              onClick={() => setShowCart(true)}
              className="bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-xs sm:text-sm px-6 sm:px-8 py-4 sm:py-5 transition-all duration-300 whitespace-nowrap"
            >
              Reserve Selected
            </Button>
          </div>
        </div>
      )}

      {/* Cart / Checkout Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-sidebar">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowCart(false)} />
          <div className="relative w-full sm:max-w-sm bg-white h-full shadow-2xl overflow-y-auto animate-fade-in-up">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-xl text-[#002366]">Your Selection</h2>
                <button data-testid="close-cart-btn" onClick={() => setShowCart(false)} className="p-1 text-[#1A2B4C]/40 hover:text-[#002366]">
                  <X size={20} />
                </button>
              </div>

              {/* Confirmed Bottles */}
              {myConfirmed.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs uppercase tracking-widest text-emerald-600 font-body mb-3">Confirmed</h3>
                  {myConfirmed.map((b) => (
                    <div key={b.number} className="flex items-center justify-between py-3 border-b border-[#D1CFC7]/50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-12 bg-[#16663A] rounded-t-full rounded-b-sm flex items-center justify-center">
                          <span className="font-display text-xs text-white font-bold">{b.number}</span>
                        </div>
                        <span className="font-body text-sm text-[#002366]">Bottle #{b.number}</span>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700 border-none text-xs">Confirmed</Badge>
                    </div>
                  ))}
                </div>
              )}

              {/* Held Bottles */}
              {myHeld.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs uppercase tracking-widest text-[#D4AF37] font-body mb-3">Pending Deposit</h3>
                  {myHeld.map((b) => (
                    <div key={b.number} className="flex items-center justify-between py-3 border-b border-[#D1CFC7]/50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-12 bg-[#D4AF37] rounded-t-full rounded-b-sm flex items-center justify-center">
                          <span className="font-display text-xs text-white font-bold">{b.number}</span>
                        </div>
                        <div>
                          <span className="font-body text-sm text-[#002366] block">Bottle #{b.number}</span>
                          <span className="font-body text-xs text-[#1A2B4C]/40">
                            {(b.selection_type || "random") === "specific"
                              ? `€${PRICE_SPECIFIC} (specific choice)`
                              : `€${PRICE_RANDOM} (random)`}
                          </span>
                        </div>
                      </div>
                      <button
                        data-testid={`release-bottle-${b.number}`}
                        onClick={() => handleRelease(b.number)}
                        className="p-1 text-[#1A2B4C]/30 hover:text-[#EA580C] transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {myHeld.length === 0 && myConfirmed.length === 0 && (
                <p className="font-body text-sm text-[#1A2B4C]/40 text-center py-8">
                  No bottles selected yet. Tap a number on the grid to choose one.
                </p>
              )}

              {/* Checkout */}
              {myHeld.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#D1CFC7]">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-body text-sm text-[#1A2B4C]/60 uppercase tracking-widest">Total Deposit</span>
                    <span className="font-display text-2xl text-[#002366]">&#8364;{calculateTotal()}</span>
                  </div>
                  <p className="font-body text-xs text-[#1A2B4C]/40 mb-4 leading-relaxed">
                    Your deposit secures your number and comes off your first bottle. Fully redeemable.
                  </p>
                  <Button
                    data-testid="checkout-btn"
                    onClick={handleCheckout}
                    disabled={checkoutLoading}
                    className="w-full bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-sm px-8 py-6 transition-all duration-300"
                  >
                    {checkoutLoading ? "Redirecting to Payment..." : "Secure My Bottles"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
